import type { LandmarkCard, TravelTip } from "../../types";

export default function CityIslandTravelSection({
  city,
  island,
  islandNickname,
  landmarkCards,
  travelTips,
}: {
  city: string;
  island?: string;
  islandNickname?: string;
  landmarkCards?: LandmarkCard[];
  travelTips?: TravelTip[];
}) {
  if (!island || (!landmarkCards?.length && !travelTips?.length)) return null;

  return (
    <section id="island-guide" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Island Banner */}
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
            Island Guide
          </p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-2">
            {city} on {island}
          </h2>
          {islandNickname && (
            <p className="text-gray-400 text-sm italic">{islandNickname}</p>
          )}
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed mt-4">
            Every Hawaiian island has its own rhythm, and {city} is no
            exception. Whether you are a local resident or a visitor, here is
            what makes this place unique — and what to keep in mind when your
            passport needs are urgent.
          </p>
        </div>

        {/* Landmarks Grid */}
        {landmarkCards && landmarkCards.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {landmarkCards.map((lm, i) => (
              <div
                key={i}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-[#F6C344]/50 transition-colors"
              >
                <div className="h-48 w-full">
                  <img
                    src={lm.imageUrl}
                    alt={lm.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-[#1A365D] text-base mb-2">
                    {lm.name}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    {lm.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Travel Tips */}
        {travelTips && travelTips.length > 0 && (
          <div className="bg-[#F7F8FA] rounded-lg p-6 md:p-8 border border-gray-200">
            <h3 className="font-bold text-[#1A365D] text-base md:text-lg mb-6 flex items-center gap-2">
              <i className="ri-compass-3-line text-[#F6C344]"></i>
              Local Travel Tips for {city} Passport Seekers
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {travelTips.map((tip, i) => (
                <div
                  key={i}
                  className="bg-white rounded-lg p-5 border border-gray-100"
                >
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${tip.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <h4 className="font-bold text-[#1A365D] text-sm mb-1">
                    {tip.title}
                  </h4>
                  <p className="text-gray-500 text-xs leading-relaxed">
                    {tip.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}